import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { reviewCardFrameSx } from './reviewStyles';
import { ReviewSummaryRow } from './ReviewSummaryRow';
import type { ReviewItem } from '../types/review-types';
import { getReviewItemAnswerText } from '../utils/reviewItems';
import { parseReviewReply } from '../utils/reviewReply';

interface ReviewResolvedCardProps {
  /** The answered group, in order; its reply is recorded on the first item. */
  items: ReviewItem[];
}

/** The answered Review card as it stays in the transcript: each item and the pick made. */
export const ReviewResolvedCard = ({ items }: ReviewResolvedCardProps) => {
  const { formatMessage } = useIntl();
  const answerText = getReviewItemAnswerText(items[0]);
  const entries = useMemo(() => {
    const picks = parseReviewReply(items, answerText);
    return items.map((item) => ({ itemId: item.id, item, pick: picks[item.id] ?? null }));
  }, [items, answerText]);
  const title = formatMessage({ id: 'epiChat.review.title', defaultMessage: 'Review' });
  const buildNewLabel = formatMessage({
    id: 'epiChat.variableMatch.buildNew',
    defaultMessage: 'Build a new variable',
  });

  return (
    <Stack spacing={2} data-testid="review-card-resolved" sx={reviewCardFrameSx}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ChecklistRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} aria-hidden />
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Stack>
      <Stack>
        {entries.map((entry) => (
          <ReviewSummaryRow key={entry.itemId} entry={entry} buildNewLabel={buildNewLabel} />
        ))}
      </Stack>
    </Stack>
  );
};
